import { Player, CourtResult } from '@/types/tournament';
import { assignPlayersToCourts, updateStandings } from '@/utils/tournamentLogic';
import { toast } from 'sonner';
import { ExtendedTournamentState } from './types';
import { generateAmericanoPartnerships, assignAmericanoCourts } from './americanoLogic';

export const createSubmitRoundResults = (
  setTournamentState: React.Dispatch<React.SetStateAction<ExtendedTournamentState>>,
  t: (key: string, params?: any) => string
) => {
  return (roundResults: CourtResult[]) => {
    setTournamentState(prevState => {
      if (roundResults.length !== prevState.courts.length) {
        toast.error(t("tournament.mexicano.incomplete_results"));
        return prevState;
      }
      
      // Update players with round scores
      const updatedPlayers = prevState.players.map(player => {
        const newPlayer = { ...player };
        
        // Find player's court and their score for this round
        prevState.courts.forEach((court, courtIndex) => {
          const result = roundResults[courtIndex];
          if (court.some(p => p.id === player.id)) {
            const isTeam1 = court.slice(0, 2).some(p => p.id === player.id);
            const roundScore = isTeam1 ? result.score[0] : result.score[1];
            
            // Update round scores array
            if (!newPlayer.roundScores) {
              newPlayer.roundScores = [];
            }
            newPlayer.roundScores[prevState.currentRound - 1] = roundScore;
          }
        });
        
        return newPlayer;
      });
      
      // Update standings with the updated players
      const finalUpdatedPlayers = updateStandings(updatedPlayers, roundResults);
      const updatedResults = [...prevState.results];
      updatedResults[prevState.currentRound - 1] = roundResults;
      
      // Store court assignments for this round with player information
      const updatedRoundCourts = [...prevState.roundCourts];
      const roundResultsWithPlayers = roundResults.map((result, courtIndex) => {
        const court = prevState.courts[courtIndex];
        return {
          ...result,
          players: {
            team1: {
              player1: court[0].name,
              player2: court[1].name
            },
            team2: {
              player1: court[2].name,
              player2: court[3].name
            }
          }
        };
      });
      updatedRoundCourts[prevState.currentRound - 1] = { courts: prevState.courts, results: roundResultsWithPlayers };
      
      console.log(`Storing Mexicano round ${prevState.currentRound} results:`, roundResultsWithPlayers);
      console.log(`Storing Mexicano round ${prevState.currentRound} court assignments:`, prevState.courts);
      
      // Always continue to next round - no limit on number of rounds
      // Check if the next round already has stored data - if so, preserve it
      const nextRoundIndex = prevState.currentRound; // currentRound is 1-based, array is 0-based
      let newCourts: Player[][];
      
      if (updatedRoundCourts[nextRoundIndex] && updatedRoundCourts[nextRoundIndex].courts.length > 0) {
        // Next round already has stored court assignments - preserve them
        newCourts = updatedRoundCourts[nextRoundIndex].courts;
        console.log(`Preserving existing court assignments for round ${prevState.currentRound + 1}:`, newCourts);
      } else {
        // Generate new court assignments for the next round
        newCourts = assignPlayersToCourts(finalUpdatedPlayers);
        // Store the new court assignments for the next round (only if not already stored)
        updatedRoundCourts[nextRoundIndex] = { courts: newCourts, results: [] };
        console.log(`Generated new court assignments for round ${prevState.currentRound + 1}:`, newCourts);
      }
      
      toast.success(t("tournament.mexicano.next_round", { round: prevState.currentRound + 1 }));
      
      return {
        ...prevState,
        players: finalUpdatedPlayers,
        courts: newCourts,
        results: updatedResults,
        roundCourts: updatedRoundCourts,
        currentRound: prevState.currentRound + 1,
        // Keep tournament active - no automatic ending
        isActive: true,
      };
    });
  };
};

export const createFinishAmericanoRound = (
  setTournamentState: React.Dispatch<React.SetStateAction<ExtendedTournamentState>>,
  language: string
) => {
  return (results: any[]) => {
    console.log('Finishing Americano round with results:', results);

    setTournamentState(prev => {
      // The results are already in the correct format with score: {team1, team2}
      const updatedMatches = results.map(result => ({
        ...result
      }));

      const updatedPlayers = [...prev.americanoPlayers];
      
      updatedMatches.forEach(match => {
        if (match.score) {
          const { team1, team2 } = match;
          const { team1: score1, team2: score2 } = match.score;

          [team1.player1, team1.player2].forEach(player => {
            const playerIndex = updatedPlayers.findIndex(p => p.id === player.id);
            if (playerIndex !== -1) {
              updatedPlayers[playerIndex].points += score1;
              updatedPlayers[playerIndex].gamesPlayed += 1;
              
              // Track round scores
              if (!updatedPlayers[playerIndex].roundScores) {
                updatedPlayers[playerIndex].roundScores = [];
              }
              updatedPlayers[playerIndex].roundScores![prev.currentRound - 1] = score1;
              
              const partnerId = player.id === team1.player1.id ? team1.player2.id : team1.player1.id;
              if (!updatedPlayers[playerIndex].partnerships.includes(partnerId)) {
                updatedPlayers[playerIndex].partnerships.push(partnerId);
              }
            }
          });

          [team2.player1, team2.player2].forEach(player => {
            const playerIndex = updatedPlayers.findIndex(p => p.id === player.id);
            if (playerIndex !== -1) {
              updatedPlayers[playerIndex].points += score2;
              updatedPlayers[playerIndex].gamesPlayed += 1;
              
              // Track round scores
              if (!updatedPlayers[playerIndex].roundScores) {
                updatedPlayers[playerIndex].roundScores = [];
              }
              updatedPlayers[playerIndex].roundScores![prev.currentRound - 1] = score2;
              
              const partnerId = player.id === team2.player1.id ? team2.player2.id : team2.player1.id;
              if (!updatedPlayers[playerIndex].partnerships.includes(partnerId)) {
                updatedPlayers[playerIndex].partnerships.push(partnerId);
              }
            }
          });
        }
      });

      // Store match assignments for this round with player information
      const updatedRoundAmericanoMatches = [...prev.roundAmericanoMatches];
      const roundResultsWithPlayers = updatedMatches.map(match => ({
        ...match,
        players: {
          team1: {
            player1: match.team1.player1.name,
            player2: match.team1.player2.name
          },
          team2: {
            player1: match.team2.player1.name,
            player2: match.team2.player2.name
          }
        }
      }));
      updatedRoundAmericanoMatches[prev.currentRound - 1] = { matches: prev.americanoMatches, results: roundResultsWithPlayers };

      // Store the completed matches for this round
      const updatedRoundResults = [...prev.americanoRoundResults];
      updatedRoundResults[prev.currentRound - 1] = updatedMatches;
      
      console.log(`Storing round ${prev.currentRound} results:`, updatedMatches);
      
      const nextRound = prev.currentRound + 1;
      
      // Check if the next round already has stored data - if so, preserve it
      const nextRoundIndex = nextRound - 1; // rounds are 1-based, array is 0-based
      let newMatches;
      
      if (updatedRoundAmericanoMatches[nextRoundIndex] && updatedRoundAmericanoMatches[nextRoundIndex].matches.length > 0) {
        // Next round already has stored match assignments - preserve them
        newMatches = updatedRoundAmericanoMatches[nextRoundIndex].matches;
        console.log(`Preserving existing match assignments for round ${nextRound}:`, newMatches);
      } else {
        // Generate new match assignments for the next round
        const partnerships = generateAmericanoPartnerships(updatedPlayers, nextRound);
        newMatches = assignAmericanoCourts(partnerships);
        // Store the new match assignments for the next round (only if not already stored)
        updatedRoundAmericanoMatches[nextRoundIndex] = { matches: newMatches, results: [] };
        console.log(`Generated new match assignments for round ${nextRound}:`, newMatches);
      }

      toast.success(language === 'no' 
        ? `Runde ${prev.currentRound} fullført! Runde ${nextRound} startet.` 
        : `Round ${prev.currentRound} completed! Round ${nextRound} started.`);

      return {
        ...prev,
        americanoPlayers: updatedPlayers,
        americanoRoundResults: updatedRoundResults,
        roundAmericanoMatches: updatedRoundAmericanoMatches,
        currentRound: nextRound,
        isRoundActive: true,
        americanoMatches: newMatches,
        // Keep tournament active - no automatic ending
        isActive: true,
        isFinished: false
      };
    });
  };
};
